/**
 * Phase 36.4 — `SubscriptionActiveGuard` 402 capture store.
 *
 * The backend's `SubscriptionActiveGuard` (Phase 36.3a, registered
 * globally via `APP_GUARD`) returns 402 Payment Required on any write
 * attempt by a merchant whose subscription is past-grace OR canceled.
 * The response body carries:
 *   { code: 'SUBSCRIPTION_PAST_GRACE' | 'SUBSCRIPTION_INACTIVE' | ...,
 *     message: string,
 *     subscriptionStatus: string,
 *     gracePeriodEndsAt?: string | null,
 *     portalRoute?: string }
 *
 * The dashboard catches 402s in the axios response interceptor
 * (`apps/dashboard/src/lib/axios.ts`) and pushes the body into this
 * store. The `BillingGuardScreen` overlay (mounted at AppShell root)
 * reads the store and renders a full-page restore-billing surface
 * with an actionable "Go to billing" CTA.
 *
 * Reads (GET/HEAD/OPTIONS) are exempt server-side so the user can
 * still see the dashboard while past-grace; writes drop here.
 */
import { create } from 'zustand';

export interface BillingGuardState {
  /** Discriminated code for branching copy. */
  code: string;
  /** Server-supplied human-readable message. */
  message: string;
  /** Current `organizations.subscription_status` value. */
  subscriptionStatus: string;
  /** Server-computed grace-window expiration; null past-grace. */
  gracePeriodEndsAt?: string | null;
  /**
   * Server-suggested deep link to the recovery surface. Today the
   * portal redirect itself is gated on Phase 36.2c, so the dashboard
   * routes to `/settings/billing` regardless. The field is stored
   * for future polish (e.g. surface "Open Stripe portal →" once
   * 36.2c lights up the click handler).
   */
  portalRoute?: string;
}

interface BillingGuardStore {
  guard: BillingGuardState | null;
  setGuard: (guard: BillingGuardState) => void;
  clearGuard: () => void;
}

export const useBillingGuardStore = create<BillingGuardStore>((set) => ({
  guard: null,
  setGuard: (guard) => set({ guard }),
  clearGuard: () => set({ guard: null }),
}));
